import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    holidayPropertiesType,
    holidayYearType,
} from '../../types';
import ExpandedListTab from '../ExpandedListTab';
import HolidayYear from './HolidayYear';
import useExtendedState from '../../hooks/useExtendedState';
import useSingleValidator from '../../hooks/useSingleValidator';
import { holidaysItemsValidator } from '../../validators/holidays';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_HOLIDAYS_QUERY_NAME,
    UPDATE_HOLIDAY_YEAR,
} from '../../graphql/holidays';
import Privileges from '../../utils/Privileges';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import { Privileged } from '../../hocs/withPrivileges';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';

HolidayDetailTab.propTypes = {
    holidayYear: holidayYearType.isRequired,
    holidayProps: holidayPropertiesType.isRequired,
};

function HolidayDetailTab(props) {
    const {
        holidayYear,
        holidayProps,
    } = props;
    const { t } = useTranslation();
    const { availableHolidayYears } = holidayProps;
    const [resetComponentsAfterModification, setResetComponentsAfterModification] = useState(false);
    const holidaysState = useExtendedState(holidayYear.holidays.map((holiday) => ({
        id: holiday.id,
        name: holiday.name,
        dateWithoutYear: holiday.holidayDate.substring(5),
        isValid: true,
    })));
    const setHolidays = (newHolidays) => {
        holidaysState.setValue(newHolidays);
        setResetComponentsAfterModification(false);
    };
    const {
        isValid: isHolidaysValid,
        validator: holidaysValidator,
    } = useSingleValidator(false, useMemo(() => holidaysItemsValidator(t), [t]));
    const isInvalidHolidayItem = holidaysState.value.some((holiday) => holiday.isValid === false) ? Promise.reject() : Promise.resolve();
    const isChangedPromise = holidaysState.isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isHolidaysValid(), isInvalidHolidayItem])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const updateHolidayYearMutation = useExtendedMutation(UPDATE_HOLIDAY_YEAR, {
        variables: {
            year: holidayYear.year,
            holidays: holidaysState.value.map((holiday) => ({
                name: holiday.name,
                holidayDate: `${holidayYear.year}-${holiday.dateWithoutYear}`,
            })),
        },
        successCallback: () => setResetComponentsAfterModification(true),
        statesToResetOnSuccess: [holidaysState],
        successMessageOptions: { key: 'holidays.notification.holidays-update-success', dataProperty: 'updateHolidayYear' },
        failureMessageOptions: { key: 'holidays.notification.holidays-update-failure', params: { year: holidayYear.year } },
        refetchQueries: [ALL_HOLIDAYS_QUERY_NAME],
    });
    return (
        <ExpandedListTab className="holidays-detail-tab" title={t('holidays.holiday-detail')}>
            <div className="rich-list-row-expanded--fields">
                <ReadonlyNumberField
                    label={t('holidays.list-header.year')}
                    value={holidayYear.year}
                    className="holidays-detail-tab--fields--year"
                    noThousandSeparator
                />
                <HolidayYear
                    year={holidayYear.year}
                    holidays={holidaysState.value}
                    availableHolidayYears={availableHolidayYears}
                    onHolidayYearChange={setHolidays}
                    isChanged={holidaysState.isChanged}
                    onReset={holidaysState.reset}
                    validator={holidaysValidator}
                    doResetComponentsAfterModification={resetComponentsAfterModification}
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateHolidayYearMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('holidays.confirm-update-holidays', { year: holidayYear.year })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle" />
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
            </Privileged>
        </ExpandedListTab>
    );
}

export default HolidayDetailTab;
